// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 10-security-compliance.md

import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ClaudeManagedSettingsCards } from '@/components/settings/ClaudeManagedSettingsCards';
import { CopilotManagedSettingsCards } from '@/components/settings/CopilotManagedSettingsCards';
import { OrgInstructionsCard } from '@/components/settings/OrgInstructionsCard';
import { StepHeader } from '@/components/install/StepHeader';
import { getIdpProvider, type IdpProviderId } from '@/lib/idpProviders';
import {
  PluginMarketplaceStatusSchema,
  type AgentKind,
  type InstallationState,
  type RoleSource,
} from '@greenlight/shared';

interface SummaryStepProps {
  org: {
    name: string;
    appsSubdomain: string | null;
  };
  /** Control-plane base URL from the install context — the MCP URL derives from it. */
  greenlightBaseUrl: string;
  installationState: InstallationState;
  /** True until every other step is complete — finishing writes `installed_at`. */
  blocked: boolean;
  /** Which agents' rollout content to surface (docs/28 §Agent setup, ADR-0040). */
  supportedAgents: AgentKind[];
  onFinish: () => void;
  isSubmitting: boolean;
}

/** The subset of the completed `idp` step's details this confirmation reads. */
interface IdpDetails {
  provider?: IdpProviderId;
  role_source?: RoleSource;
  role_groups?: { org_admin?: string[] };
  admin_group_id?: string;
  initial_admin_email?: string;
}

/** Marketplace repositories, newest schema first, falling back to the pre-multi-repo shape. */
function marketplaceRepos(state: InstallationState): string[] {
  const details = state.steps?.plugin_marketplace?.details;
  const parsed = PluginMarketplaceStatusSchema.array().safeParse(details?.['repositories']);
  if (parsed.success && parsed.data.length > 0) return parsed.data.map((r) => r.repo_full_name);
  const legacy = details?.['repo_full_name'];
  return typeof legacy === 'string' && legacy.length > 0 ? [legacy] : [];
}

function idpDetails(state: InstallationState): IdpDetails {
  return (state.steps?.idp?.details ?? {}) as IdpDetails;
}

/** Google has no group claim, so it is always Greenlight-managed (docs/10). */
function roleSourceOf(details: IdpDetails): RoleSource {
  if (details.provider === 'google') return 'greenlight';
  if (details.role_source) return details.role_source;
  return (details.role_groups?.org_admin?.length ?? 0) > 0 || details.admin_group_id
    ? 'idp'
    : 'greenlight';
}

export function SummaryStep({
  org,
  greenlightBaseUrl,
  installationState,
  blocked,
  supportedAgents,
  onFinish,
  isSubmitting,
}: SummaryStepProps) {
  const navigate = useNavigate();
  const mcpUrl = `${greenlightBaseUrl.replace(/\/+$/, '')}/mcp`;
  const repos = marketplaceRepos(installationState);
  const idp = idpDetails(installationState);
  const roleSource = roleSourceOf(idp);
  const adminGroup = idp.role_groups?.org_admin?.[0] ?? idp.admin_group_id;
  const showClaudeRollout = repos.length > 0 && supportedAgents.includes('claude');
  const showCopilotRollout = repos.length > 0 && supportedAgents.includes('copilot');

  return (
    <div className="space-y-6" data-testid="step-summary">
      <StepHeader step="summary" title="Installation complete">
        Confirm what Greenlight configured, roll it out to your team, then finish. The same
        instructions always live under Settings → Agent setup.
      </StepHeader>

      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight">Your Greenlight install</h2>
        <dl
          className="divide-y divide-border rounded-lg border text-sm"
          data-testid="install-review"
        >
          <Row label="Organization" value={org.name} />
          <Row label="Greenlight host" value={org.appsSubdomain} />
          <Row label="Identity provider" value={getIdpProvider(idp.provider)?.label} />
          <Row
            label="Role management"
            value={
              idp.provider ? (roleSource === 'idp' ? 'IdP-managed' : 'Greenlight-managed') : null
            }
          />
          {roleSource === 'idp' ? (
            <Row label="Org admin group" value={adminGroup} />
          ) : (
            <Row label="Initial administrator" value={idp.initial_admin_email} />
          )}
          {repos.length > 0 && (
            <div className="flex flex-col sm:flex-row px-4 py-3 gap-1">
              <dt className="text-muted-foreground w-full sm:w-44 shrink-0">
                Marketplace {repos.length > 1 ? 'repositories' : 'repository'}
              </dt>
              <dd className="min-w-0 space-y-1 font-medium" data-testid="install-review-repos">
                {repos.map((repo) => (
                  <MonoValue key={repo} copyable wrap>
                    {repo}
                  </MonoValue>
                ))}
              </dd>
            </div>
          )}
          <Row label="Org MCP URL" value={mcpUrl} />
        </dl>
        <p className="text-xs text-muted-foreground">
          The MCP URL is what every supported agent connects to.
        </p>
      </section>

      {showClaudeRollout ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Claude rollout</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Install Greenlight for your Claude organization, pre-approve it, then tell Claude when
              to use it. Codex, Cursor and Copilot steps are under{' '}
              <Link to="/settings/agent-setup" className="underline underline-offset-4">
                Settings → Agent setup
              </Link>
              .
            </p>
          </div>
          <ClaudeManagedSettingsCards />
          <OrgInstructionsCard agent="claude" mcpUrl={mcpUrl} />
        </section>
      ) : null}

      {showCopilotRollout ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Copilot rollout</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enable MCP servers in Copilot, merge the managed-settings snippet so the Greenlight
              plugin and this install&rsquo;s MCP URL are allowed, then pre-approve Greenlight.
              Claude, Codex and Cursor steps are under{' '}
              <Link to="/settings/agent-setup" className="underline underline-offset-4">
                Settings → Agent setup
              </Link>
              .
            </p>
          </div>
          <CopilotManagedSettingsCards marketplaceRepo={repos[0]!} mcpUrl={mcpUrl} />
        </section>
      ) : null}

      {!showClaudeRollout && !showCopilotRollout ? (
        <Alert>
          <AlertTitle>Agent setup details</AlertTitle>
          <AlertDescription>
            Each agent&rsquo;s rollout steps, from installing Greenlight for the organization to
            pre-approving it, are under{' '}
            <Link to="/settings/agent-setup" className="font-medium underline underline-offset-4">
              Settings → Agent setup
            </Link>
            .
          </AlertDescription>
        </Alert>
      ) : null}

      {blocked ? (
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          Finishing stays available until every earlier step is complete. Go back to the first
          incomplete step to continue the install.
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            className="w-full sm:w-auto h-11 md:h-9"
            onClick={onFinish}
            disabled={isSubmitting}
            data-testid="step-finish"
          >
            {isSubmitting ? 'Finishing…' : 'Finish setup'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full sm:w-auto h-11 md:h-9"
            onClick={() => navigate('/apps')}
            data-testid="step-go-to-apps"
          >
            Go to Apps
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
      <dt className="text-muted-foreground w-full sm:w-44 shrink-0">{label}</dt>
      <dd className="font-medium min-w-0">
        <MonoValue copyable wrap>
          {value}
        </MonoValue>
      </dd>
    </div>
  );
}
